import { Controller, useForm } from 'react-hook-form';


import { Stack } from 'ui/stack';

import Modal from 'components/partials/modal/modal';
import Input from 'components/partials/input/input';
import FilterItem from '../filter-item/filter-item';

import type { JsonNode } from 'api/generated/schemas';

import { useCurrentUser } from 'hooks/api/use-current-user';
import {
  useCreateFavoriteReport,
  useUpdateFavoriteReport,
} from 'hooks/api/use-favorite-reports';

import type { FavoriteReportData } from '../farovite-report-tile/farovite-report-tile';
import type { FavoriteReportFilter } from '../filter-item/filter-item';

interface FavoriteMetadata extends JsonNode {
  filters?: FavoriteReportFilter[];
}

interface FavoriteModalFormValues {
  name: string;
}

interface FavoriteModalFormProps {
  reportId: string | number;
  metadata?: FavoriteMetadata | null;
  favoriteReport?: FavoriteReportData | null;
  bookmarkState?: string | null;
  onClose: () => void;
  onSuccess?: () => void;
}

const FavoriteModalForm = ({
  reportId,
  metadata = null,
  favoriteReport = null,
  bookmarkState,
  onClose,
  onSuccess,
}: FavoriteModalFormProps): React.JSX.Element => {
  const currentUser = useCurrentUser();
  const createMutation = useCreateFavoriteReport();
  const updateMutation = useUpdateFavoriteReport();
  const {
    control,
    handleSubmit,
    formState: { isValid, isDirty, isSubmitting },
  } = useForm<FavoriteModalFormValues>({
    mode: 'onChange',
    defaultValues: {
      name: favoriteReport?.name ?? '',
    },
  });

  const handleUpdateFavoriteReport = async (
    data: FavoriteModalFormValues
  ): Promise<void> => {
    if (!favoriteReport?.bookmarkState) return;

    await updateMutation.mutateAsync({
      oktaUserId: currentUser.data!.oktaUserId,
      userId: Number(currentUser.data!.id),
      bookmarkId: Number(favoriteReport!.id),
      data: {
        reportId: Number(reportId),
        name: data.name,
        bookmarkState: favoriteReport.bookmarkState,
        metadata: favoriteReport!.metadata ?? undefined,
      },
    });

    onClose();
    onSuccess?.();
  };

  const handleCreateFavoriteReport = async (
    data: FavoriteModalFormValues
  ): Promise<void> => {
    if (!bookmarkState) return;

    await createMutation.mutateAsync({
      oktaUserId: currentUser.data!.oktaUserId,
      userId: Number(currentUser.data!.id),
      data: {
        reportId: Number(reportId),
        name: data.name,
        bookmarkState,
        metadata: metadata ?? undefined,
      },
    });

    onClose();
  };

  const onSubmit = async (data: FavoriteModalFormValues): Promise<void> => {
    if (favoriteReport?.id) {
      await handleUpdateFavoriteReport(data);
    } else {
      await handleCreateFavoriteReport(data);
    }
  };

  return (
    <Modal
      headerText={favoriteReport?.id ? 'Rename favorite' : 'Save new favorite'}
      onConfirmButtonLabel="Save"
      onConfirmCallback={handleSubmit(onSubmit)}
      onCloseCallback={onClose}
      disabled={!isValid || !isDirty}
      loading={isSubmitting}
    >
      <div className="space-y-6">
        <Controller
          name="name"
          control={control}
          rules={{
            required: 'Please enter a name for the favorite',
            maxLength: {
              value: 100,
              message: 'Name must be less than 100 characters',
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              label="Item name"
              labelFor="name"
              placeholder="Add favorite name..."
              labelInTop
              customClass="w-full"
              errorsNames={error}
            />
          )}
        />

        {metadata?.filters && metadata.filters.length > 0 && (
          <div className="space-y-2">
            <p className="text-md font-semibold text-gray-700">Filters</p>
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
              {metadata.filters.map((filter) => (
                <FilterItem key={filter.column} filter={filter} />
              ))}
            </Stack>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default FavoriteModalForm;
